/**
 * Apex Match as a responsibility spec — the slot/apex defender's multi-threat read.
 *
 * The busiest read in the engine. The apex keys #2 but is also responsible for #1 if #1 works
 * short into his area, for the back if he releases to this side, and (in trips) for a switch
 * receiver when #2 breaks out. See `.kiro/steering/cover3Match.md` and `cover6.md`.
 *
 * ## What changed in the migration
 *
 * The hand-written policy pre-scanned ALL THREE feeds up front and then picked its assignment
 * from that full-play knowledge:
 *
 *     const k2      = classifyApexKey2(key2Feed, params);
 *     const wr1Tick = classifyKey1Short(key1Feed, params);   // scans the whole route
 *     if (k2.kind === 'vertical') { if (wr1Tick !== null) action = 'MAN1'; ... }
 *
 * So on a play where #2 ran vertical at 0.6s and #1 broke short at 2.0s, the apex committed to
 * #1 from the outset and simply IGNORED #2's vertical — because it already knew #1 would come
 * open later. That is a different failure from the Palms one: not just acting early, but
 * covering the wrong receiver entirely.
 *
 * As a spec, each threat is a transition evaluated per tick. He reacts to whichever he actually
 * SEES first, and can be pulled off it later by a higher-priority threat that then declares.
 * Declaration order below is the priority ladder for threats seen on the same tick.
 */

import { ApexMatchParams, Position } from '../../types';
import {
  KeyObservables,
  isHardBreak,
  isSettled,
  isComingBackToLos,
  isBreakingToward,
  landmarkFor,
} from '../observables';
import { ReadContext, ReadAction, ResponsibilitySpec } from '../responsibility';

/** Partner slots, in the order the resolver supplies them. */
const KEY1 = 0;
const RB = 1;
const SWITCH = 2;

/** Depth at which #2, still on his stem, counts as a vertical threat — off his own alignment. */
const verticalLineOf = (key: KeyObservables, p: ApexMatchParams) =>
  landmarkFor(key, p.verticalDepthYards) + p.breakDepthToleranceY;

/** Trail a receiver from just underneath — the apex's man technique on every assignment. */
const trail = (obs: KeyObservables, params: ApexMatchParams, speed: number): ReadAction => ({
  target: { x: obs.position.x, y: obs.position.y + params.trailCushion } as Position,
  speed,
});

/** #2 breaking shallow toward the middle — a short in-breaker the apex passes off. */
const key2Inside = (obs: KeyObservables, p: ApexMatchParams) =>
  isHardBreak(obs, p.breakAngleDeg) &&
  !isComingBackToLos(obs, p.backToLosSpeed) &&
  obs.position.y > verticalLineOf(obs, p) &&
  isBreakingToward(obs, p.insideSignX, p.lateralEps);

/** #2 breaking shallow toward the sideline — the trips "switch" trigger. */
const key2ShortOut = (obs: KeyObservables, p: ApexMatchParams) =>
  isHardBreak(obs, p.breakAngleDeg) &&
  !isComingBackToLos(obs, p.backToLosSpeed) &&
  obs.position.y > verticalLineOf(obs, p) &&
  isBreakingToward(obs, -p.insideSignX, p.lateralEps);

/** #1 working short into the apex's area — a shallow out, a short in-breaker, or a curl settling back. */
const key1Short = (obs: KeyObservables | undefined, p: ApexMatchParams) => {
  if (!obs) return false;
  if (obs.position.y <= landmarkFor(obs, p.key1DeepLineYards)) return false; // went deep — not the apex's problem
  const shortOut =
    isHardBreak(obs, p.breakAngleDeg) && isBreakingToward(obs, p.key1OutSignX, p.lateralEps);
  // A short IN-breaker (slant/drag) cutting toward the middle while still shallow. Gated on the
  // SHORT line, not just the deep line: a Dig breaks in at ~10 yds (deeper than the short mark)
  // and a Post climbs from its break, so both stay below this ceiling and remain the corner's /
  // safety's problem. `isBreakingToward(-key1OutSignX)` is the mirror of the short-out direction.
  const shortIn =
    isHardBreak(obs, p.breakAngleDeg) &&
    isBreakingToward(obs, -p.key1OutSignX, p.lateralEps) &&
    obs.position.y > landmarkFor(obs, p.key1ShortLineYards);
  const curl =
    obs.position.y <= landmarkFor(obs, p.key1ShortLineYards) &&
    (isComingBackToLos(obs, p.backToLosSpeed) || isSettled(obs, p.settleSpeedFraction));
  return shortOut || shortIn || curl;
};

/** The back releasing to this defender's side while still shallow. */
const rbToMySide = (obs: KeyObservables | undefined, p: ApexMatchParams) =>
  !!obs &&
  (obs.position.x - p.rbStartX) * p.rbFlatSignX > p.rbFlatThresholdX &&
  obs.position.y > p.rbFlatDepthY;

export const APEX_MATCH_SPEC: ResponsibilitySpec<ApexMatchParams> = {
  id: 'apex-match',
  initialTechnique: 'backpedal',
  // His DEFAULT assignment is #2 — if nothing else declares, he simply covers his key. Starting
  // in READ would leave him uncommitted on a play where no threat ever materialises, which is
  // not what the apex does.
  initialState: 'MAN',

  latches: {
    // ORDER MATTERS. The interpreter evaluates latches in declaration order, and #2's read is
    // decided by the FIRST thing he does: a receiver who breaks shallow inside is an in-breaker
    // for the rest of the play, even though a slant keeps running and will cross the vertical
    // line later. Declaring the shallow breaks first, and gating the vertical on them, is what
    // makes that first-event-wins semantics explicit.
    key2Inside: ({ key, params }) => key2Inside(key, params),
    key2ShortOut: ({ key, params }) => key2ShortOut(key, params),
    /** #2 got vertical WITHOUT having broken off shallow first. Then the apex is locked to him. */
    key2Vertical: ({ key, params, latches }) =>
      !latches.key2Inside && !latches.key2ShortOut && key.position.y <= verticalLineOf(key, params),
    key1Short: ({ partners, params }) => key1Short(partners[KEY1], params),
    rbFlat: ({ partners, params }) => rbToMySide(partners[RB], params),
  },

  bindings: {
    /**
     * Who he has come off #2 to cover, if anyone. Priority when several declare on the same
     * tick mirrors the original ladder: the back first, then #1, then the switch receiver.
     * A vertical by #2 outranks all of them and blocks the binding entirely.
     *
     * The doc's "cover #1 on a short in-breaker only if no one is in the flat" is enforced by
     * this RB-before-#1 order WHEN both declare on the same tick. Across ticks the binding is
     * sticky (see the interpreter): whoever the apex saw FIRST is who he commits to — a #1
     * in-breaker showing at 0.2s is taken even if the back leaks to the flat at 0.6s. That is
     * the engine's perception-first model, not full-play omniscience, and it is deliberate.
     */
    covering: ({ latches, partners }) => {
      if (latches.key2Vertical) return null; // locked on #2
      if (latches.rbFlat && partners[RB]) return RB;
      if (latches.key1Short && partners[KEY1]) return KEY1;
      if (latches.key2ShortOut && partners[SWITCH]) return SWITCH;
      return null;
    },
  },

  transitions: [
    {
      note: '#2 got vertical — come back out of the zone and carry him',
      from: ['READ', 'ZONE'],
      to: 'MAN',
      when: ({ latches }) => latches.key2Vertical,
    },
    {
      note: 'came off #2 onto the back, #1, or the switch receiver',
      from: ['READ', 'ZONE'],
      to: 'MAN',
      when: ({ bindings }) => bindings.covering !== null,
    },
    {
      // Fires from MAN because manning #2 is his default state, not something he transitions
      // into. #2 breaking short inside is what takes him OFF that default.
      note: '#2 broke short inside with nobody else to take — pass him off, sink to the zone',
      from: ['READ', 'MAN'],
      to: 'ZONE',
      when: ({ latches, bindings }) =>
        latches.key2Inside && bindings.covering === null && !latches.key2Vertical,
    },
  ],

  behavior: {
    // Pre-commit: trail #2 at a controlled pace while reading everything else.
    READ: ({ key, params }) => trail(key, params, params.readSpeed),

    ZONE: ({ params }) => ({ target: params.zoneLandmark, speed: params.zoneDropSpeed }),

    // Cover whoever he committed to — the bound partner, or #2 if he never came off.
    MAN: (ctx) => {
      const target = ctx.bound('covering') ?? ctx.key;
      return trail(target, ctx.params, ctx.params.maxSpeed);
    },
  },
};

/** Re-typed helper so the resolver can pass its feeds in the slot order this spec expects. */
export const APEX_PARTNER_ORDER = { KEY1, RB, SWITCH };

export type ApexReadContext = ReadContext<ApexMatchParams>;
